import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const TicketCategories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const appVersion = import.meta.env.VITE_VERSION;

  // Updated category data with new colors and responsive icon sizes
  const categoryData = {
    'TROUBLESHOOTING': {
      title: 'Troubleshooting',
      description: 'Submit a ticket to resolve an issue/repairs.',
      icon: (
        <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        </svg>
      ),
      path: '/troubleshooting',
      color: '#2563eb',
      gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)'
    },
    'ACCOUNT_MANAGEMENT': {
      title: 'Account Management',
      description: 'Requests for DepEd and o365 account creation, password resets or updates.',
      icon: (
        <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
      ),
      path: '/account',
      color: '#7c3aed',
      gradient: 'linear-gradient(135deg, #7c3aed, #8b5cf6)'
    },
    'DOCUMENT_UPLOAD': {
      title: 'Uploading of Publication',
      description: 'Assistance in submitting and uploading of publications.',
      icon: (
        <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.31.083l-3-2.5a1 1 0 011.122-1.65l1.877.626z" clipRule="evenodd" />
        </svg>
      ),
      path: '/documents',
      color: '#059669',
      gradient: 'linear-gradient(135deg, #059669, #10b981)'
    },
    'TECHNICAL_ASSISTANCE': {
      title: 'Technical Assistance',
      description: 'Assistance with IT Related concerns and others.',
      icon: (
        <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      ),
      path: '/technical-assistance',
      color: '#dc2626',
      gradient: 'linear-gradient(135deg, #dc2626, #ef4444)'
    }
  };

  useEffect(() => {
    // Use hardcoded categories
    const categories = [
      { id: 1, name: 'TROUBLESHOOTING', active: true },
      { id: 2, name: 'ACCOUNT_MANAGEMENT', active: true },
      { id: 3, name: 'DOCUMENT_UPLOAD', active: true },
      { id: 4, name: 'TECHNICAL_ASSISTANCE', active: true }
    ];
    setCategories(categories);
    setLoading(false);
  }, []);

  const handleCategoryClick = (path, isActive) => {
    if (isActive) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If no categories are available, show a message
  if (categories.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-16">
        <div className="text-center mt-16">
          <h1 className="text-2xl font-bold mb-4 font-poppins">
            Ticket categories are not available
          </h1>
          <p className="text-gray-600 mb-8 font-poppins">
            Our ticket submission system cannot load the categories. Please try again later or contact support directly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen h-auto flex flex-col bg-white relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-50 z-0"
        style={{ backgroundImage: 'url("/background.jpg")' }}
      />

      {/* Header */}
      <div className="py-3 sm:py-4 px-4 sm:px-8 bg-white/95 border-b border-gray-300 text-black rounded-none z-[5] sticky top-0 backdrop-blur-md">
        <div className="flex items-center max-w-7xl mx-auto gap-3 sm:gap-4 relative w-full">
          <button
            className="flex sm:hidden mr-2 p-2 hover:bg-gray-100 rounded-lg"
            onClick={() => navigate(-1)}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <img 
              src="/deped-logo.png" 
              alt="DepEd Logo" 
              className="h-10 sm:h-12"
            />
            <h1 className="font-extrabold text-slate-800 text-lg sm:text-xl font-poppins tracking-tight">
              Ticketing System
            </h1>
          </div>
          <button
            className="ml-auto text-slate-600 font-semibold hidden sm:flex font-poppins hover:bg-slate-100 hover:text-slate-800 px-3 py-2 rounded-lg transition-colors"
            onClick={() => navigate('/track-ticket')}
          >
            Track Ticket
          </button>
          <button 
            aria-label="Admin Login"
            className="text-slate-900 bg-slate-100/80 hover:bg-slate-200 p-2 rounded-lg transition-colors"
            onClick={() => navigate('/login')}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto py-2 sm:py-4 px-4 sm:px-6 flex-1 flex flex-col relative z-10">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6 mt-0 sm:mt-2 bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 w-full max-w-2xl mx-auto shadow-lg border border-gray-200">
          <h1 className="font-bold text-gray-900 text-xl sm:text-2xl mb-3 font-poppins">
            Submit a Ticket
          </h1>
          <p className="text-gray-700 text-sm sm:text-base font-medium font-poppins">
            Please select the appropriate category for your service request
          </p>
        </div>

        {error && (
          <div className="mb-4 sm:mb-6 rounded-lg bg-white/98 p-4 border-l-4 border-red-500">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-700 font-poppins">{error}</p>
            </div>
          </div>
        )}

        {/* Service Categories */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex-1 flex flex-col shadow-lg border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-4xl mx-auto w-full">
            {categories.map((category) => {
              const data = categoryData[category.name];
              if (!data) return null;
              
              return (
                <div key={category.id} className="col-span-1">
                  <div
                    className={`p-4 sm:p-6 h-full min-h-[200px] sm:min-h-[220px] flex flex-col items-center justify-center transition-all duration-300 relative overflow-hidden rounded-2xl sm:rounded-3xl bg-white border border-black/8 ${
                      category.active 
                        ? 'cursor-pointer opacity-100 hover:-translate-y-2 hover:shadow-xl hover:shadow-black/10' 
                        : 'cursor-not-allowed opacity-60'
                    }`}
                    onClick={() => handleCategoryClick(data.path, category.active)}
                  >
                    {/* Top border gradient */}
                    <div 
                      className="absolute top-0 left-0 right-0 h-1 opacity-0 transition-opacity duration-300"
                      style={{ background: data.gradient }}
                    />
                    
                    <div className="bg-slate-50 rounded-2xl w-16 sm:w-20 h-16 sm:h-20 flex items-center justify-center mb-4 sm:mb-6 transition-all duration-300 hover:scale-110">
                      <div className="text-slate-600 transition-colors duration-300" style={{ color: category.active ? data.color : undefined }}>
                        {data.icon}
                      </div>
                    </div>
                    
                    <h3 className="font-semibold text-slate-900 mb-2 text-lg sm:text-xl font-poppins text-center transition-colors duration-300" style={{ color: category.active ? data.color : undefined }}>
                      {data.title}
                    </h3>
                    
                    <p className="text-slate-600 text-sm sm:text-base text-center leading-relaxed font-poppins max-w-[90%] mx-auto">
                      {category.active ? data.description : "This category is currently disabled"}
                    </p>
                    
                    {category.active && (
                      <div className="mt-4 flex items-center text-sm font-medium font-poppins opacity-0 transform translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0" style={{ color: data.color }}>
                        Select Category
                        <span className="ml-1 inline-block transition-transform duration-300 group-hover:translate-x-1">
                          →
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-2 sm:mt-4 mb-2 sm:mb-4 text-center">
          <p className="text-gray-600 text-xs sm:text-sm inline-block px-3 py-2 sm:px-4 sm:py-2 rounded-full bg-white/98 shadow-sm transition-all duration-200 font-poppins hover:bg-white cursor-pointer">
            Need help choosing? Contact our support team
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 sm:py-6 px-4 sm:px-6 text-center bg-slate-50/80 backdrop-blur-md border-t border-slate-200 text-slate-600 z-10 mt-auto">
        <div className="max-w-4xl mx-auto">
          <div className="mb-2 flex justify-center items-center gap-2">
            <h3 className="font-bold text-slate-700 text-base font-poppins">
              DepEd Imus City
            </h3>
          </div>
          
          <p className="text-sm font-poppins mb-2">
            For support inquiries: icts.imus@deped.gov.ph
          </p>
          
          <p className="text-sm font-poppins text-slate-400">
            © {new Date().getFullYear()} Department of Education - Imus City Division
          </p>

          <p className="text-xs sm:text-sm font-poppins text-slate-400 mt-1 italic">
            version {appVersion}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TicketCategories;  